import type { PaletteOptions } from '@mui/material/styles';
import {
  backgroundColors,
  lightBackgroundColors,
  lightInputColors,
  lightTextColors,
  statusColors,
  severityColors,
  neutral,
  textColors,
  brand,
} from '@/tokens/colors';

/**
 * Severity palette shared by both modes. These map to the security-specific
 * semantic colors from Figma and are exposed as a custom `severity` palette
 * key (see theme augmentation).
 */
export const severityPalette = {
  critical: { main: severityColors.critical, contrastText: '#FFFFFF' },
  high: { main: severityColors.high, contrastText: '#FFFFFF' },
  medium: { main: severityColors.medium, contrastText: '#1A1A1A' },
  low: { main: severityColors.low, contrastText: '#1A1A1A' },
  info: { main: severityColors.info, contrastText: '#FFFFFF' },
  untriaged: { main: severityColors.untriaged, contrastText: '#1A1A1A' },
} as const;

/** Dark palette - the primary, Figma-accurate theme. */
export const darkPalette: PaletteOptions = {
  mode: 'dark',
  primary: {
    main: brand.main,
    contrastText: '#06201C',
  },
  /**
   * The system has one accent, so `secondary` is the brand rather than a second
   * hue. It used to be `brand.link`, which meant any component rendered with
   * `color="secondary"` — a Pagination item, a Badge, a Switch — painted link
   * ink as a background fill.
   *
   * MUI requires the slot, so it cannot simply be dropped: omitting it falls
   * back to MUI's default purple, which is further off-system than what it
   * replaced. Aliasing it to the brand makes `color="secondary"` a harmless
   * synonym for `color="primary"` instead of a way to introduce a colour the
   * design system does not have.
   *
   * `contrastText` matches `primary` for the same reason it is dark there:
   * white on brand teal is 2.2:1.
   */
  secondary: {
    main: brand.main,
    contrastText: '#06201C',
  },
  error: { main: statusColors.failure, contrastText: '#FFFFFF' },
  warning: { main: statusColors.warning, contrastText: '#1A1A1A' },
  info: { main: statusColors.info, contrastText: '#FFFFFF' },
  success: { main: statusColors.success, contrastText: '#FFFFFF' },
  background: {
    default: backgroundColors.primary,
    paper: backgroundColors.widget,
  },
  text: {
    primary: textColors.primary,
    secondary: textColors.secondary,
    disabled: textColors.disabled,
  },
  divider: backgroundColors.border,
  grey: {
    50: neutral[50],
    100: neutral[100],
    200: neutral[200],
    300: neutral[300],
    400: neutral[400],
    500: neutral[500],
    600: neutral[600],
    700: neutral[700],
    800: neutral[800],
    900: neutral[900],
  },
  severity: severityPalette,
  action: {
    hover: 'rgba(255, 255, 255, 0.06)',
    selected: 'rgba(0, 196, 172, 0.16)',
    disabled: textColors.disabled,
    disabledBackground: 'rgba(255, 255, 255, 0.08)',
  },
};

/** Light palette - derived for completeness; dark remains the default. */
export const lightPalette: PaletteOptions = {
  mode: 'light',
  primary: {
    main: '#00897B',
    contrastText: '#FFFFFF',
  },
  /** Brand rather than a second hue, for the reasons on the dark palette. */
  secondary: {
    main: '#00897B',
    contrastText: '#FFFFFF',
  },
  // Option A Light §03: field error outline darkened to clear 3:1 on white.
  error: { main: lightInputColors.errorBorder, contrastText: '#FFFFFF' },
  warning: { main: statusColors.warning, contrastText: '#1A1A1A' },
  info: { main: statusColors.info, contrastText: '#FFFFFF' },
  success: { main: statusColors.success, contrastText: '#FFFFFF' },
  background: {
    // Same rung mapping as dark: body is `primary`, paper is `widget`.
    default: lightBackgroundColors.primary,
    paper: lightBackgroundColors.widget,
  },
  text: {
    primary: lightTextColors.primary,
    secondary: lightTextColors.secondary,
    disabled: lightTextColors.disabled,
  },
  divider: lightBackgroundColors.border,
  severity: severityPalette,
};
